import { daysBetween, parseISODate } from '~/features/shared/calendar/dateUtils';
import {
  CalendarEvent,
  CalendarEventType,
  CalendarFilters,
  CalendarLayerState,
  CalendarStatusLevel,
} from './types';

export {
  addDays,
  buildMonthGrid,
  buildWeekDays,
  daysBetween,
  endOfMonth,
  formatDisplayDate,
  parseISODate,
  startOfMonth,
  startOfWeek,
  toISODate,
} from '~/features/shared/calendar/dateUtils';

// All date math operates on local calendar days via Y/M/D parts so that
// all-day milestones never shift because of timezone conversion.
const DUE_SOON_WINDOWS: Partial<Record<CalendarEventType, number>> = {
  CONTRACT_START: 14,
  CONTRACT_END: 30,
  LISTING_END: 14,
  B2B_CONTRACT_START: 30,
  B2B_CONTRACT_END: 60,
};

/** Visual status level for an event, relative to `today`. */
export const getStatusLevel = (event: CalendarEvent, today: Date): CalendarStatusLevel => {
  const eventDay = parseISODate(event.eventDate);
  const delta = daysBetween(today, eventDay);

  if (event.credentialReadinessState === 'blocked') return 'blocked';
  if (
    event.credentialReadinessState === 'warning' &&
    (event.eventType === 'CONTRACT_START' || event.eventType === 'B2B_CONTRACT_START') &&
    delta >= 0
  ) {
    return 'warning';
  }
  if (delta < 0) {
    return ['COMPLETED', 'TERMINATED', 'CLOSED', 'EXPIRED', 'FILLED'].includes(event.status)
      ? 'completed'
      : 'normal';
  }
  const window = DUE_SOON_WINDOWS[event.eventType];
  if (window !== undefined && delta <= window) return 'dueSoon';
  return 'normal';
};

const LAYER_BY_TYPE: Record<CalendarEventType, keyof CalendarLayerState> = {
  CONTRACT_START: 'contracts',
  CONTRACT_END: 'contracts',
  LISTING_START: 'listings',
  LISTING_END: 'listings',
  B2B_CONTRACT_START: 'b2b',
  B2B_CONTRACT_END: 'b2b',
  BILLING_DATE: 'billing',
};

const isB2BEvent = (e: CalendarEvent): boolean =>
  e.eventType === 'B2B_CONTRACT_START' ||
  e.eventType === 'B2B_CONTRACT_END' ||
  e.listingType === 'MSAAS';

export const filterEvents = (
  events: CalendarEvent[],
  layers: CalendarLayerState,
  filters: CalendarFilters
): CalendarEvent[] =>
  events.filter((e) => {
    if (!layers[LAYER_BY_TYPE[e.eventType]]) return false;
    if (e.status === 'DRAFT' && !layers.drafts) return false;
    if (filters.facility !== 'all' && e.facilityName !== filters.facility) return false;
    if (filters.contractType !== 'all') {
      if (filters.contractType === 'MSAAS' && !isB2BEvent(e)) return false;
      if (filters.contractType === 'JOB' && isB2BEvent(e)) return false;
    }
    if (filters.b2bRole !== 'all' && isB2BEvent(e) && e.employerRole !== filters.b2bRole) {
      return false;
    }
    if (filters.search) {
      const q = filters.search.toLowerCase();
      const haystack = [e.title, e.counterpartyDisplay, e.facilityName, e.contractNumber]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      if (!haystack.includes(q)) return false;
    }
    return true;
  });

const EVENT_TYPE_ORDER: Record<CalendarEventType, number> = {
  CONTRACT_START: 0,
  B2B_CONTRACT_START: 1,
  LISTING_START: 2,
  CONTRACT_END: 3,
  B2B_CONTRACT_END: 4,
  LISTING_END: 5,
  BILLING_DATE: 6,
};

/** Group events by ISO date; within a day, starts sort before ends, billing last. */
export const groupEventsByDate = (events: CalendarEvent[]): Map<string, CalendarEvent[]> => {
  const map = new Map<string, CalendarEvent[]>();
  events.forEach((e) => {
    const list = map.get(e.eventDate) ?? [];
    list.push(e);
    map.set(e.eventDate, list);
  });
  map.forEach((list) =>
    list.sort((a, b) => {
      const typeOrder = EVENT_TYPE_ORDER[a.eventType] - EVENT_TYPE_ORDER[b.eventType];
      if (typeOrder !== 0) return typeOrder;
      const titleOrder = a.title.localeCompare(b.title);
      if (titleOrder !== 0) return titleOrder;
      return a.eventId.localeCompare(b.eventId);
    })
  );
  return map;
};

export const EVENT_TYPE_LABELS: Record<CalendarEventType, string> = {
  CONTRACT_START: 'Contract start',
  CONTRACT_END: 'Contract end',
  LISTING_START: 'Listing start',
  LISTING_END: 'Listing end',
  B2B_CONTRACT_START: 'B2B contract start',
  B2B_CONTRACT_END: 'B2B contract end',
  BILLING_DATE: 'Billing date',
};

export const uniqueFacilities = (events: CalendarEvent[]): string[] =>
  Array.from(new Set(events.map((e) => e.facilityName).filter((f): f is string => !!f))).sort();
